import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { CheckCircle, Loader2, AlertTriangle, ShoppingCart } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATES = {
  CHECKING: 'checking',
  PAID: 'paid',
  PENDING: 'pending',
  EXPIRED: 'expired',
  ERROR: 'error',
};

const MAX_ATTEMPTS = 5;
const POLL_INTERVAL_MS = 2000;

const PaymentSuccess = () => {
  const [params] = useSearchParams();
  const sessionId = params.get('session_id');
  const [state, setState] = useState(STATES.CHECKING);
  const [details, setDetails] = useState(null);
  const [attempts, setAttempts] = useState(0);

  const pollStatus = useCallback(async (attempt) => {
    try {
      const { data } = await axios.get(`${API}/orders/payments/status/${sessionId}`);
      setDetails(data);
      if (data.payment_status === 'paid') {
        setState(STATES.PAID);
        return;
      }
      if (data.status === 'expired') {
        setState(STATES.EXPIRED);
        return;
      }
      if (attempt + 1 >= MAX_ATTEMPTS) {
        setState(STATES.PENDING);
        return;
      }
      setTimeout(() => {
        setAttempts(attempt + 1);
        pollStatus(attempt + 1);
      }, POLL_INTERVAL_MS);
    } catch (e) {
      console.error('Payment status poll failed:', e);
      setState(STATES.ERROR);
    }
  }, [sessionId]);

  useEffect(() => {
    if (!sessionId) {
      setState(STATES.ERROR);
      return;
    }
    pollStatus(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return (
    <div
      data-testid="payment-success-page"
      className="min-h-screen bg-gradient-to-b from-gray-50 to-white py-16 flex items-center"
    >
      <div className="max-w-xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
        <Card className="border-2 shadow-2xl">
          <CardContent className="pt-12 pb-12 text-center">
            {state === STATES.CHECKING && (
              <>
                <Loader2 className="w-20 h-20 text-[#FDB913] mx-auto mb-6 animate-spin" />
                <h2 className="text-3xl font-bold text-gray-900 mb-3">Confirming your payment...</h2>
                <p className="text-gray-600 mb-2">Stripe is processing your transaction.</p>
                <p className="text-sm text-gray-500">Check {attempts + 1} of {MAX_ATTEMPTS}</p>
              </>
            )}

            {state === STATES.PAID && (
              <>
                <CheckCircle className="w-20 h-20 text-green-500 mx-auto mb-6" />
                <h2 className="text-3xl font-bold text-gray-900 mb-3" data-testid="payment-paid-headline">
                  Payment Successful!
                </h2>
                <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                  Thank you — your order is now <strong>paid</strong>. We'll be in touch shortly to schedule your winemaking session.
                </p>
                {details?.amount_total && (
                  <p className="text-sm text-gray-500 mb-6">
                    Charged: <strong>{(details.amount_total / 100).toFixed(2)} {details.currency?.toUpperCase()}</strong>
                  </p>
                )}
                <Link to="/">
                  <Button className="bg-[#FDB913] hover:bg-[#E5A50A] text-gray-900 font-semibold">
                    Back to Home
                  </Button>
                </Link>
              </>
            )}

            {state === STATES.PENDING && (
              <>
                <Loader2 className="w-20 h-20 text-orange-500 mx-auto mb-6" />
                <h2 className="text-3xl font-bold text-gray-900 mb-3">Payment is still processing</h2>
                <p className="text-gray-600 mb-6">
                  This sometimes takes a few minutes. We'll email you the moment it's confirmed. You can safely close this page.
                </p>
                <Link to="/">
                  <Button variant="outline">Back to Home</Button>
                </Link>
              </>
            )}

            {state === STATES.EXPIRED && (
              <>
                <AlertTriangle className="w-20 h-20 text-orange-500 mx-auto mb-6" />
                <h2 className="text-3xl font-bold text-gray-900 mb-3">Payment session expired</h2>
                <p className="text-gray-600 mb-6">
                  Please contact The Lokal Winery so we can resend you a fresh payment link.
                </p>
                <a href="mailto:info@thelokal.ca">
                  <Button className="bg-[#FDB913] hover:bg-[#E5A50A] text-gray-900 font-semibold">
                    Email Us
                  </Button>
                </a>
              </>
            )}

            {state === STATES.ERROR && (
              <>
                <AlertTriangle className="w-20 h-20 text-red-500 mx-auto mb-6" />
                <h2 className="text-3xl font-bold text-gray-900 mb-3">Something went wrong</h2>
                <p className="text-gray-600 mb-6">
                  We couldn't verify your payment status right now. If you completed payment, you'll still receive an email confirmation.
                </p>
                <Link to="/order">
                  <Button variant="outline">
                    <ShoppingCart className="w-4 h-4 mr-2" />
                    Back to Orders
                  </Button>
                </Link>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PaymentSuccess;
